import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ModalForm } from "@/components/ModalForm";
import { Search, FileText, Sparkles, Globe } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

interface SaveQueryDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSave: (name: string, description?: string, isPublic?: boolean) => Promise<void>;
	isLoading: boolean;
	queryPreview?: string;
	initialName?: string;
	initialDescription?: string;
	initialIsPublic?: boolean;
	isUpdate?: boolean;
}

export function SaveQueryDialog({
	open,
	onOpenChange,
	onSave,
	isLoading,
	queryPreview,
	initialName = '',
	initialDescription = '',
	initialIsPublic = false,
	isUpdate = false
}: SaveQueryDialogProps) {
	const { t } = useTranslation();
	const [name, setName] = useState(initialName);
	const [description, setDescription] = useState(initialDescription);
	const [isPublic, setIsPublic] = useState(initialIsPublic);

	// Update state when initial values change
	useEffect(() => {
		if (open) {
			setName(initialName);
			setDescription(initialDescription || '');
			setIsPublic(initialIsPublic);
		}
	}, [open, initialName, initialDescription, initialIsPublic]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;
		await onSave(name.trim(), description.trim() || undefined, isPublic);
	};

	const handleClose = () => {
		setName(initialName);
		setDescription(initialDescription || '');
		setIsPublic(initialIsPublic);
		onOpenChange(false);
	};

	const title = isUpdate
		? t("queries.update.title", "Update Query")
		: t("queries.save.title", "Save Query");

	const buttonText = isUpdate
		? t("queries.update.button", "Update Query")
		: t("queries.save.saveButton", "Save Query");

	return (
		<ModalForm
			title={title}
			isOpen={open}
			onClose={handleClose}
			onSubmit={handleSubmit}
			submitText={isLoading ? t("savedQueries.form.creating", "Saving...") : buttonText}
			cancelText={t("common.cancel", "Cancel")}
			submitDisabled={!name.trim() || isLoading}
			className="max-w-2xl"
		>
			{/* Visual Header */}
			<div className="text-center mb-8">
				<div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 dark:bg-purple-900/30 rounded-sm mb-4">
					<Search className="h-8 w-8 text-purple-600" />
				</div>
				<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
					{title}
				</h3>
				<p className="text-gray-600 dark:text-gray-300 text-sm">
					{isUpdate
						? t("queries.update.description", "Update your query settings")
						: t("queries.save.description", "Save your query to use it again later")}
				</p>
			</div>

			{/* Form Fields */}
			<div className="space-y-6">
				{/* Name Field */}
				<div className="space-y-2">
					<Label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
						<FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
						{t("queries.save.name", "Query Name")} *
					</Label>
					<Input
						id="name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder={t("queries.save.namePlaceholder", "Enter a name for this query")}
						className="h-11 text-base"
						required
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{t("savedQueries.form.nameHelp", "Choose a descriptive name for your saved query")}
					</p>
				</div>

				{/* Description Field */}
				<div className="space-y-2">
					<Label htmlFor="description" className="text-sm font-medium text-gray-700 dark:text-gray-200 flex items-center gap-2">
						<Sparkles className="h-4 w-4 text-gray-500 dark:text-gray-400" />
						{t("queries.save.descriptionLabel", "Description")}
					</Label>
					<Textarea
						id="description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder={t("queries.save.descriptionPlaceholder", "Optional description")}
						className="min-h-[80px] text-base resize-none"
						rows={3}
					/>
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{t("savedQueries.form.descriptionHelp", "Optional description to help you remember what this query is for")}
					</p>
				</div>

				{/* Public Toggle */}
				<div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-sm">
					<div className="flex items-center gap-3">
						<Globe className="h-5 w-5 text-gray-500 dark:text-gray-400" />
						<div>
							<Label htmlFor="isPublic" className="text-sm font-medium text-gray-700 dark:text-gray-200">
								{t("queries.save.public", "Make public to team")}
							</Label>
							<p className="text-xs text-gray-500 dark:text-gray-400">
								{t("queries.save.publicDescription", "Allow team members to use this query")}
							</p>
						</div>
					</div>
					<Switch
						id="isPublic"
						checked={isPublic}
						onCheckedChange={setIsPublic}
					/>
				</div>

				{/* Query Preview */}
				{queryPreview && (
					<Card className="border-dashed border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
						<CardContent className="p-4">
							<div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
								{t("queries.save.queryPreview", "Query to save")}
							</div>
							<div className="text-sm font-mono bg-white dark:bg-gray-800 px-3 py-2 rounded-sm border text-gray-600 dark:text-gray-300 overflow-x-auto">
								{queryPreview}
							</div>
						</CardContent>
					</Card>
				)}
			</div>
		</ModalForm>
	);
}
